import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Heading3, Select, Tabs } from '@treams/treams-ui';

import { DashboardEngagementNoDataAvailable } from './engagement/DashboardEngagementNoDataAvailable';
import DashboardEngagementQuestions from './engagement/DashboardEngagementQuestions';
import DashboardEngagementTopics from './engagement/DashboardEngagementTopics';
import AllScoresTable from './engagement/metrics/AllScoresTable';
import EngagementMetric from './engagement/metrics/EngagementMetric';
import OverallScoreMetric from './engagement/metrics/OverallScoreMetric';
import ParticipationRatesMetric from './engagement/metrics/ParticipationRatesMetric';
import NpsAnalytics from './NpsAnalytics';

enum EngagementTab {
	Overview,
	Nps,
	Topics,
	Feedback,
	Matrix,
}

const Column = styled(Box)`
	flex-shrink: 0;
`;

function DashboardEngagementMetrics(props: {
	data:
		| graphql.WorkspaceEngagementMetricsQuery
		| graphql.EngagementOfficerMetricsQuery
		| graphql.GroupEngagementMetricsQuery;
	filters: graphql.AnalyticsFilters;
	tab: EngagementTab;
}) {
	const { t } = useTranslation();

	const data =
		'workspaceAnalytics' in props.data
			? props.data.workspaceAnalytics
			: 'groupAnalytics' in props.data
				? props.data.groupAnalytics
				: props.data.engagementAnalytics;

	const participation =
		data.engagementGroupsParticipation as graphql.AnalyticsEngagementGroupParticipation[];

	if (props.tab === EngagementTab.Overview) {
		return (
			<>
				<Flex flexWrap="wrap" mt={3}>
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<OverallScoreMetric
								data={data.engagementScoreOverTime as graphql.AnalyticsEngagementScore}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<ParticipationRatesMetric
								data={
									data.engagementParticipationOverTime as graphql.AnalyticsEngagementParticipation
								}
							/>
						</Box>
					</Column>
				</Flex>

				<Heading3 mt={4} mb={3}>
					{t('dashboard.engagement.thrivingTopics.title')}
				</Heading3>
				<Flex flexWrap="wrap" mt={3}>
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementThrivingSubcategories[0] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementThrivingSubcategories[1] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
				</Flex>

				<Heading3 mt={4} mb={3}>
					{t('dashboard.engagement.decliningTopics.title')}
				</Heading3>
				<Flex flexWrap="wrap">
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementDecliningSubcategories[0] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementDecliningSubcategories[1] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
				</Flex>
			</>
		);
	}

	if (props.tab === EngagementTab.Topics) {
		return (
			<Box mt={3}>
				<DashboardEngagementTopics filters={props.filters} headless />
			</Box>
		);
	}

	if (props.tab === EngagementTab.Nps) {
		return (
			<Box mt={3}>
				<NpsAnalytics filters={props.filters} />
			</Box>
		);
	}

	if (props.tab === EngagementTab.Feedback) {
		return (
			<Box mt={3}>
				<DashboardEngagementQuestions filters={props.filters} headless />
			</Box>
		);
	}

	if (props.tab === EngagementTab.Matrix) {
		return data.engagementGroupsSubcategoriesScore.length > 0 ? (
			<Box mt={5}>
				<AllScoresTable
					scores={
						data.engagementGroupsSubcategoriesScore as graphql.AnalyticsEngagementGroupSubcategoryScore[]
					}
					participation={[
						...participation,
						{
							participation: data.engagementParticipationOverTime.average ?? undefined,
							delta: data.engagementParticipationOverTime.delta ?? undefined,
						},
					]}
				/>
			</Box>
		) : (
			<Box mt={3}>
				<DashboardEngagementNoDataAvailable />
			</Box>
		);
	}

	return null;
}

function WorkspaceEngagement(props: { filters: graphql.AnalyticsFilters; tab: EngagementTab }) {
	const query = graphql.useWorkspaceEngagementMetricsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} tab={props.tab} filters={props.filters} />;
}

function GroupEngagement(props: { filters: graphql.AnalyticsFilters; tab: EngagementTab }) {
	const query = graphql.useGroupEngagementMetricsQuery({
		variables: {
			filters: props.filters,
		},
		skip: !props.filters.groupIDs || props.filters.groupIDs.length === 0,
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} tab={props.tab} filters={props.filters} />;
}

function EngagementOfficerEngagement(props: {
	filters: graphql.AnalyticsFilters;
	tab: EngagementTab;
}) {
	const query = graphql.useEngagementOfficerMetricsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} tab={props.tab} filters={props.filters} />;
}

function EngagementAnalyticsContents(props: {
	filters: graphql.AnalyticsFilters;
	tab: EngagementTab;
	interval: string;
}) {
	const userRoles = useViewerRoles();

	if (userRoles.isEngagementOfficer) {
		return <EngagementOfficerEngagement filters={props.filters} tab={props.tab} />;
	}

	if (userRoles.isWorkspaceSuperuser) {
		return <WorkspaceEngagement filters={props.filters} tab={props.tab} />;
	}

	return <GroupEngagement filters={props.filters} tab={props.tab} />;
}

export default function EngagementAnalytics(props: {
	filters: graphql.AnalyticsFilters;
	eNPS?: boolean;
	interval: string;
	onRequestIDChange?: (requestID: string | undefined) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const { location, push } = useHistory();
	const { params } = useRouteMatch<{
		metricGroupID: string;
		metricID: string;
		tabID?: string;
		departmentID?: string;
	}>();
	const tabKey = _.upperFirst(_.camelCase(params.tabID));
	const selectedEngagementTemplateId =
		new URLSearchParams(location.search).get('engagementTemplate') ?? undefined;
	const selectedEngagementRequestId =
		new URLSearchParams(location.search).get('engagementRequest') ?? undefined;

	const tab = Object.values(EngagementTab).includes(tabKey)
		? EngagementTab[tabKey]
		: props.eNPS
			? EngagementTab.Nps
			: EngagementTab.Overview;

	const setTab = (newTab: EngagementTab) => {
		const newTabKey = _.kebabCase(EngagementTab[newTab]);
		const search = location.search;

		push(`/analytics/metric/${params.metricGroupID}/${params.metricID}/${newTabKey}${search}`);
	};

	const [templateID, setTemplateID] = useState<string | undefined>(selectedEngagementTemplateId);
	const [requestID, setRequestID] = useState<string | undefined>(selectedEngagementRequestId);

	const { data, error } = graphql.useEngagementTemplateListingQuery({
		variables: { first: 1000 },
		fetchPolicy: 'cache-first',
	});

	const { data: requestsData } = graphql.useEngagementRequestListingQuery({
		variables: {
			templateID: templateID ?? undefined,
			first: 1000,
		},
		skip: !templateID,
		fetchPolicy: 'cache-first',
	});

	useEffect(() => {
		if (!templateID) {
			setRequestID(undefined);
		}
	}, [templateID]);

	useEffect(() => {
		const hasRequests = templateID && requestID && requestsData;

		if (hasRequests) {
			const requestsMatchCurrentTemplate = requestsData.engagementRequestListing.edges.some(
				({ node }) => node.id === requestID,
			);
			if (!requestsMatchCurrentTemplate) {
				setRequestID(undefined);
			}
		}
	}, [requestsData]);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);

		if (templateID) {
			searchParams.set('engagementTemplate', templateID);
		} else {
			searchParams.delete('engagementTemplate');
		}

		if (requestID) {
			searchParams.set('engagementRequest', requestID);
		} else {
			searchParams.delete('engagementRequest');
		}

		// Only update if changed, avoids history bloating
		const newSearch = searchParams.toString();
		if (location.search.replace(/^\?/, '') !== newSearch) {
			push({ ...location, search: newSearch ? `?${newSearch}` : '' });
		}
	}, [templateID, requestID]);

	useEffect(() => {
		props.onRequestIDChange?.(requestID);
	}, [requestID]);

	return (
		<>
			<Flex alignItems="flex-end">
				{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

				<Heading3 flexGrow={1} flexShrink={1}>
					{t('analytics.metric.detailsTitle')}
				</Heading3>
				<Box mr={2}>
					<Select
						selectedOption={
							templateID
								? undefined
								: {
										title: t('analytics.metric.engagement.filters.template.all'),
										value: undefined,
									}
						}
						selectedValue={templateID}
						options={
							data
								? [
										{
											title: t('analytics.metric.engagement.filters.template.all'),
											value: undefined,
										},
										undefined,
										..._.sortBy(
											data?.engagementTemplateListing.edges.map(({ node }) => ({
												title: localize(node.title).text,
												value: node.id,
											})),
											'title',
										),
									]
								: undefined
						}
						onChange={(value) => setTemplateID(value!)}
					/>
				</Box>
				{templateID && (
					<Box mr={2}>
						<Select
							selectedOption={
								requestID
									? undefined
									: {
											title: t('analytics.metric.engagement.filters.request.all'),
											value: undefined,
										}
							}
							selectedValue={requestID}
							options={
								requestsData
									? [
											{
												title: t('analytics.metric.engagement.filters.request.all'),
												value: undefined,
											},
											undefined,
											..._.sortBy(
												requestsData?.engagementRequestListing.edges.map(({ node }) => {
													const date = moment(node.createdAt).format('LLL');

													return {
														title: node.isCancelled
															? `${date} — ${t('analytics.metric.engagement.filters.request.cancelled')}`
															: date,
														value: node.id,
														createdAt: node.createdAt,
													};
												}),
												'createdAt',
											).reverse(),
										]
									: undefined
							}
							onChange={(value) => setRequestID(value!)}
						/>
					</Box>
				)}
			</Flex>
			<Tabs>
				<Flex ml={-3}>
					<Tabs.Item
						title={t('dashboard.engagement.overview.title')}
						onClick={() => setTab(EngagementTab.Overview)}
						selected={tab === EngagementTab.Overview}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.nps.title')}
						onClick={() => setTab(EngagementTab.Nps)}
						selected={tab === EngagementTab.Nps}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.topics.title')}
						onClick={() => setTab(EngagementTab.Topics)}
						selected={tab === EngagementTab.Topics}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.questions.title')}
						onClick={() => setTab(EngagementTab.Feedback)}
						selected={tab === EngagementTab.Feedback}
					/>
					<Tabs.Item
						title={t('dashboard.engagement.matrix.title')}
						onClick={() => setTab(EngagementTab.Matrix)}
						selected={tab === EngagementTab.Matrix}
					/>
				</Flex>
			</Tabs>
			<EngagementAnalyticsContents
				filters={{
					...props.filters,
					engagementTemplateIDs: templateID ? [templateID] : undefined,
					engagementRequestID: requestID,
				}}
				interval={props.interval}
				tab={tab}
			/>
		</>
	);
}
